import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { selectUser } from "../../store/selectors";
import axiosInstance from "../../api/axiosInstance";
import { STATIC_PAGES_SAVED_EVENT } from "../../api/cms/useStaticPagesApi";
import styles from "./Sidebar.module.css";

import {
  LayoutDashboard,
  Globe,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Users,
  User,
  UserCheck,
  List,
  XCircle,
  Flag,
  Map,
  Building2,
  Umbrella,
  Plane,
  Layers,
  LayoutTemplate,
  FileText,
  HelpCircle,
  Mail,
  Settings,
  Truck,
  Package,
  AlertCircle,
  Wifi,
  CalendarClock,
  Armchair,
  Ticket,
  Settings2,
  Hotel,
  BookOpen,
  PlusCircle,
  KeyRound,
  Activity,
  CalendarDays,
  Briefcase,
  Database,
  RotateCcw,
  Inbox,
  Ruler,
  BadgeDollarSign,
  Tag,
  Calculator,
  CreditCard,
  ShieldCheck,
  Building,
  Hash,
  Server,
  LifeBuoy,
  FolderOpen,
  FileText as FileInvoice,
  Car,
  Wallet,
  DollarSign,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  Receipt,
  Percent,
  FileBarChart,
  ClipboardCheck,
  Files,
  FileWarning,
  History,
  ScrollText,
  Wrench,
} from "lucide-react";

// One Static Pages menu entry per page that can be opened, in CMS order.
const toMenuPages = (pages) =>
  (Array.isArray(pages) ? pages : [])
    .filter((p) => p && p.slug && p.title)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

const Sidebar = () => {
  const user = useAppSelector(selectUser);
  const [collapsed, setCollapsed] = useState(false);

  const [bookingsOpen, setBookingsOpen] = useState(false);
  const [geoOpen, setGeoOpen] = useState(false);
  const [suppliersOpen, setSuppliersOpen] = useState(false);

  const [peopleOpen, setPeopleOpen] = useState(false);
  const [customersOpen, setCustomersOpen] = useState(false);
  const [travelersOpen, setTravelersOpen] = useState(false);

  const [productsOpen, setProductsOpen] = useState(false);
  const [flightsOpen, setFlightsOpen] = useState(false);
  const [hotelsOpen, setHotelsOpen] = useState(false);
  const [cmsOpen, setCmsOpen] = useState(false);
  const [cmsLayoutOpen, setCmsLayoutOpen] = useState(false);
  const [staticPagesOpen, setStaticPagesOpen] = useState(false);

  // The Static Pages sub-menu mirrors the PAGES defined in the CMS — one entry
  // per page (About SUNSKY, Terms & Traveller Rights, …). Adding or renaming a
  // page there updates this menu with no code change. Failing to load just
  // leaves the parent item as a plain link.
  //
  // The sidebar is mounted once for the whole session, so the list is loaded
  // once and then replaced by every save in the editor. Loading it on mount
  // alone froze it at the pages that existed when the dashboard was opened.
  const [staticGroups, setStaticGroups] = useState([]);
  useEffect(() => {
    let stale = false;
    axiosInstance
      .get("/cms/static-pages", { params: { all: true } })
      .then((res) => {
        if (!stale) setStaticGroups(toMenuPages(res?.data?.data?.pages));
      })
      .catch(() => { /* menu degrades to a single Static Pages link */ });

    const onSaved = (e) => {
      // A save is newer than the load above, which must not land on top of it.
      stale = true;
      setStaticGroups(toMenuPages(e.detail?.pages));
    };
    window.addEventListener(STATIC_PAGES_SAVED_EVENT, onSaved);
    return () => {
      stale = true;
      window.removeEventListener(STATIC_PAGES_SAVED_EVENT, onSaved);
    };
  }, []);

  const [pricingOpen, setPricingOpen] = useState(false);
  const [systemOpen, setSystemOpen] = useState(false);
  // Users & Permissions menu is temporarily hidden from the sidebar (feature
  // built but not yet exposed in navigation). Re-enable by uncommenting this
  // state and the menu block further down.
  // const [usersOpen, setUsersOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [vatOpen, setVatOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const isActivePrefix = (prefix) => location.pathname.startsWith(prefix);

  return (
    <div className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""}`}>
      <div className={styles.header}>
        <div className={styles.headerBg} />
        <div className={styles.brand}>
          <div className={styles.logoGlow} />
          <img
            src={require("../../assets/new-logo.png")}
            alt="Sunsky"
            className={styles.logoImg}
          />
          {!collapsed && (
            <>
              <div className={styles.brandRight}>
                <span className={styles.wordmark}>Sunsky</span>
                <span className={styles.brandSub}>Travel Platform</span>
              </div>
            </>
          )}
        </div>
        <button
          className={styles.collapseBtn}
          onClick={() => setCollapsed((p) => !p)}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <div className={styles.menu}>

        {/* ── Dashboard ── */}
        <div
          className={`${styles.item} ${isActive("/dashboard") ? styles.active : ""}`}
          onClick={() => navigate("/dashboard")}
        >
          <LayoutDashboard size={18} />
          {!collapsed && <span>Dashboard</span>}
        </div>

        {/* ── Bookings ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/bookings") ? styles.active : ""}`}
          onClick={() => setBookingsOpen((p) => !p)}
        >
          <CalendarDays size={18} />
          {!collapsed && <span>Bookings</span>}
          {!collapsed && (bookingsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {bookingsOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${isActive("/bookings") ? styles.activeSub : ""}`}
              onClick={() => navigate("/bookings")}
            >
              <List size={14} />
              All Bookings
            </div>
            <div
              className={`${styles.subitem} ${isActive("/bookings/add") ? styles.activeSub : ""}`}
              onClick={() => navigate("/bookings/add")}
            >
              <PlusCircle size={14} />
              Add Booking
            </div>
            <div
              className={`${styles.subitem} ${isActive("/bookings/imported") ? styles.activeSub : ""}`}
              onClick={() => navigate("/bookings/imported")}
            >
              <Inbox size={14} />
              Imported Bookings
            </div>
            <div
              className={`${styles.subitem} ${isActive("/bookings/cancellation") ? styles.activeSub : ""}`}
              onClick={() => navigate("/bookings/cancellation")}
            >
              <RotateCcw size={13} />
              Cancellation Flow
            </div>
          </div>
        )}

        {/* ── People ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/people") ? styles.active : ""}`}
          onClick={() => setPeopleOpen((p) => !p)}
        >
          <Users size={18} />
          {!collapsed && <span>People</span>}
          {!collapsed && (peopleOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {peopleOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${styles.subitemGroup} ${isActivePrefix("/people/customers") ? styles.activeGroup : ""}`}
              onClick={() => setCustomersOpen((p) => !p)}
            >
              <Briefcase size={14} />
              Customers
              {customersOpen ? <ChevronDown size={13} className={styles.groupChevron} /> : <ChevronRight size={13} className={styles.groupChevron} />}
            </div>

            {customersOpen && (
              <div className={styles.submenu}>
                <div
                  className={`${styles.subitem} ${isActive("/people/customers/private") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/people/customers/private")}
                >
                  <User size={13} />
                  Private
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/people/customers/professional") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/people/customers/professional")}
                >
                  <Building2 size={13} />
                  Professional
                </div>
              </div>
            )}

            <div
              className={`${styles.subitem} ${styles.subitemGroup} ${isActivePrefix("/people/travelers") ? styles.activeGroup : ""}`}
              onClick={() => setTravelersOpen((p) => !p)}
            >
              <Plane size={14} />
              Travelers
              {travelersOpen ? <ChevronDown size={13} className={styles.groupChevron} /> : <ChevronRight size={13} className={styles.groupChevron} />}
            </div>

            {travelersOpen && (
              <div className={styles.submenu}>
                <div
                  className={`${styles.subitem} ${isActive("/people/travelers/profiles") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/people/travelers/profiles")}
                >
                  <UserCheck size={13} />
                  Travelers
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/people/travelers/rules") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/people/travelers/rules")}
                >
                  <Ruler size={13} />
                  Traveler Rules
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Products ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/products") ? styles.active : ""}`}
          onClick={() => setProductsOpen((p) => !p)}
        >
          <Package size={18} />
          {!collapsed && <span>Products</span>}
          {!collapsed && (productsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {productsOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${styles.subitemGroup}`}
              onClick={() => setFlightsOpen((p) => !p)}
            >
              <Plane size={14} />
              Flights
              {flightsOpen ? <ChevronDown size={13} className={styles.groupChevron} /> : <ChevronRight size={13} className={styles.groupChevron} />}
            </div>

            {flightsOpen && (
              <div className={styles.submenu}>
                <div
                  className={`${styles.subitem} ${isActive("/products/flights/airlines") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/flights/airlines")}
                >
                  <Plane size={13} />
                  Airlines
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/products/flights/flight-status") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/flights/flight-status")}
                >
                  <AlertCircle size={13} />
                  Flight Status
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/products/flights/ssr") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/flights/ssr")}
                >
                  <Wifi size={13} />
                  SSR
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/products/flights/cabin-classes") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/flights/cabin-classes")}
                >
                  <Armchair size={13} />
                  Cabin Classes
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/products/flights/fare-classes") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/flights/fare-classes")}
                >
                  <Ticket size={13} />
                  Fare Classes
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/products/flights/fare-options") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/flights/fare-options")}
                >
                  <Settings2 size={13} />
                  Fare Options
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/products/flights/schedule-updater") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/flights/schedule-updater")}
                >
                  <CalendarClock size={13} />
                  Schedule Updater
                </div>
              </div>
            )}

            <div
              className={`${styles.subitem} ${styles.subitemGroup}`}
              onClick={() => setHotelsOpen((p) => !p)}
            >
              <Hotel size={14} />
              Hotels
              {hotelsOpen ? <ChevronDown size={13} className={styles.groupChevron} /> : <ChevronRight size={13} className={styles.groupChevron} />}
            </div>

            {hotelsOpen && (
              <div className={styles.submenu}>
                <div
                  className={`${styles.subitem} ${isActive("/products/hotels/management") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/hotels/management")}
                >
                  <Hotel size={13} />
                  Management
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/products/hotels/mapping") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/products/hotels/mapping")}
                >
                  <Layers size={13} />
                  Mapping
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── GeoData ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/geodata") ? styles.active : ""}`}
          onClick={() => setGeoOpen((p) => !p)}
        >
          <Globe size={18} />
          {!collapsed && <span>GeoData</span>}
          {!collapsed && (geoOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {geoOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${isActive("/geodata/countries") ? styles.activeSub : ""}`}
              onClick={() => navigate("/geodata/countries")}
            >
              <Flag size={14} />
              Countries
            </div>
            <div
              className={`${styles.subitem} ${isActive("/geodata/regions") ? styles.activeSub : ""}`}
              onClick={() => navigate("/geodata/regions")}
            >
              <Map size={14} />
              Regions
            </div>
            <div
              className={`${styles.subitem} ${isActive("/geodata/cities") ? styles.activeSub : ""}`}
              onClick={() => navigate("/geodata/cities")}
            >
              <Building2 size={14} />
              Cities
            </div>
            <div
              className={`${styles.subitem} ${isActive("/geodata/zones") ? styles.activeSub : ""}`}
              onClick={() => navigate("/geodata/zones")}
            >
              <Layers size={14} />
              Zones
            </div>
            <div
              className={`${styles.subitem} ${isActive("/geodata/holidaytypes") ? styles.activeSub : ""}`}
              onClick={() => navigate("/geodata/holidaytypes")}
            >
              <Umbrella size={14} />
              Holiday Types
            </div>
            <div
              className={`${styles.subitem} ${isActive("/geodata/airports") ? styles.activeSub : ""}`}
              onClick={() => navigate("/geodata/airports")}
            >
              <Plane size={14} />
              Airports
            </div>
            <div
              className={`${styles.subitem} ${isActive("/geodata/destination-airports") ? styles.activeSub : ""}`}
              onClick={() => navigate("/geodata/destination-airports")}
            >
              <Plane size={14} />
              Destination airports
            </div>
          </div>
        )}

        {/* ── Suppliers ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/suppliers") ? styles.active : ""}`}
          onClick={() => setSuppliersOpen((p) => !p)}
        >
          <Truck size={18} />
          {!collapsed && <span>Suppliers</span>}
          {!collapsed && (suppliersOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {suppliersOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${isActive("/suppliers/management") ? styles.activeSub : ""}`}
              onClick={() => navigate("/suppliers/management")}
            >
              <BookOpen size={14} />
              Supplier Management
            </div>
            <div
              className={`${styles.subitem} ${isActive("/suppliers/api-credentials") ? styles.activeSub : ""}`}
              onClick={() => navigate("/suppliers/api-credentials")}
            >
              <KeyRound size={14} />
              API Credentials
            </div>
            <div
              className={`${styles.subitem} ${isActive("/suppliers/health") ? styles.activeSub : ""}`}
              onClick={() => navigate("/suppliers/health")}
            >
              <Activity size={14} />
              Health Monitoring
            </div>
          </div>
        )}

        {/* ── Pricing & Rules ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/pricing") ? styles.active : ""}`}
          onClick={() => setPricingOpen((p) => !p)}
        >
          <BadgeDollarSign size={18} />
          {!collapsed && <span>Pricing & Rules</span>}
          {!collapsed && (pricingOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {pricingOpen && !collapsed && (
          <div className={styles.submenu}>
            {/* First in the list because it is the one central place the commercial
                margin for every product is set. */}
            <div
              className={`${styles.subitem} ${isActive("/pricing/margin-engine") ? styles.activeSub : ""}`}
              onClick={() => navigate("/pricing/margin-engine")}
            >
              <BadgeDollarSign size={14} />
              Margin Engine
            </div>
            <div
              className={`${styles.subitem} ${isActive("/pricing/markups") ? styles.activeSub : ""}`}
              onClick={() => navigate("/pricing/markups")}
            >
              <Tag size={14} />
              Markups
            </div>
            <div
              className={`${styles.subitem} ${isActive("/pricing/pricing-logic") ? styles.activeSub : ""}`}
              onClick={() => navigate("/pricing/pricing-logic")}
            >
              <Calculator size={14} />
              Pricing Logic
            </div>
            <div
              className={`${styles.subitem} ${isActive("/pricing/deposit-payment") ? styles.activeSub : ""}`}
              onClick={() => navigate("/pricing/deposit-payment")}
            >
              <CreditCard size={14} />
              Deposit & Payment
            </div>
            <div
              className={`${styles.subitem} ${isActive("/pricing/insurance") ? styles.activeSub : ""}`}
              onClick={() => navigate("/pricing/insurance")}
            >
              <ShieldCheck size={14} />
              Insurance Pricing
            </div>
          </div>
        )}

        {/* ── Finance ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/finance") ? styles.active : ""}`}
          onClick={() => setFinanceOpen((p) => !p)}
        >
          <DollarSign size={18} />
          {!collapsed && <span>Finance</span>}
          {!collapsed && (financeOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {financeOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${isActive("/finance/accounts") ? styles.activeSub : ""}`}
              onClick={() => navigate("/finance/accounts")}
            >
              <Wallet size={14} />
              Accounts
            </div>

            <div
              className={`${styles.subitem} ${styles.subitemGroup} ${isActivePrefix("/finance/payments") ? styles.activeGroup : ""}`}
              onClick={() => setPaymentsOpen((p) => !p)}
            >
              <CreditCard size={14} />
              Payments
              {paymentsOpen ? <ChevronDown size={13} className={styles.groupChevron} /> : <ChevronRight size={13} className={styles.groupChevron} />}
            </div>

            {paymentsOpen && (
              <div className={styles.submenu}>
                <div
                  className={`${styles.subitem} ${isActive("/finance/payments/incoming") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/finance/payments/incoming")}
                >
                  <ArrowDownCircle size={13} />
                  Incoming Payments
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/finance/payments/outgoing") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/finance/payments/outgoing")}
                >
                  <ArrowUpCircle size={13} />
                  Outgoing Payments
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/finance/payments/refunds") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/finance/payments/refunds")}
                >
                  <RefreshCw size={13} />
                  Refunds
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/finance/payments/methods") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/finance/payments/methods")}
                >
                  <CreditCard size={13} />
                  Payment Methods
                </div>
              </div>
            )}

            <div
              className={`${styles.subitem} ${isActive("/finance/receipts") ? styles.activeSub : ""}`}
              onClick={() => navigate("/finance/receipts")}
            >
              <Receipt size={14} />
              Payment Receipts
            </div>

            <div
              className={`${styles.subitem} ${styles.subitemGroup} ${isActivePrefix("/finance/vat") ? styles.activeGroup : ""}`}
              onClick={() => setVatOpen((p) => !p)}
            >
              <Percent size={14} />
              VAT Management
              {vatOpen ? <ChevronDown size={13} className={styles.groupChevron} /> : <ChevronRight size={13} className={styles.groupChevron} />}
            </div>

            {vatOpen && (
              <div className={styles.submenu}>
                <div
                  className={`${styles.subitem} ${isActive("/finance/vat/settings") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/finance/vat/settings")}
                >
                  <Settings size={13} />
                  VAT Settings
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/finance/vat/separate-services") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/finance/vat/separate-services")}
                >
                  <Layers size={13} />
                  Separate Services
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/finance/vat/package-services") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/finance/vat/package-services")}
                >
                  <Package size={13} />
                  Package Services
                </div>
              </div>
            )}

            <div
              className={`${styles.subitem} ${isActive("/finance/transactions") ? styles.activeSub : ""}`}
              onClick={() => navigate("/finance/transactions")}
            >
              <FileBarChart size={14} />
              Transaction List
            </div>
          </div>
        )}

        {/* ── Documents ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/documents") ? styles.active : ""}`}
          onClick={() => setDocumentsOpen((p) => !p)}
        >
          <FolderOpen size={18} />
          {!collapsed && <span>Documents</span>}
          {!collapsed && (documentsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {documentsOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${isActive("/documents") ? styles.activeSub : ""}`}
              onClick={() => navigate("/documents")}
            >
              <Files size={14} />
              All Documents
            </div>
            <div
              className={`${styles.subitem} ${isActivePrefix("/documents/invoices") ? styles.activeSub : ""}`}
              onClick={() => navigate("/documents/invoices")}
            >
              <FileInvoice size={14} />
              Invoices
            </div>
            <div
              className={`${styles.subitem} ${isActivePrefix("/documents/vouchers") ? styles.activeSub : ""}`}
              onClick={() => navigate("/documents/vouchers")}
            >
              <Receipt size={14} />
              Vouchers
            </div>
            <div
              className={`${styles.subitem} ${isActivePrefix("/documents/booking-documents") ? styles.activeSub : ""}`}
              onClick={() => navigate("/documents/booking-documents")}
            >
              <ClipboardCheck size={14} />
              Booking Documents
            </div>
            <div
              className={`${styles.subitem} ${isActivePrefix("/documents/missing-travel-documents") ? styles.activeSub : ""}`}
              onClick={() => navigate("/documents/missing-travel-documents")}
            >
              <FileWarning size={14} />
              Missing Travel Documents
            </div>
            <div
              className={`${styles.subitem} ${isActivePrefix("/documents/dispatch-history") ? styles.activeSub : ""}`}
              onClick={() => navigate("/documents/dispatch-history")}
            >
              <History size={14} />
              Dispatch History
            </div>
          </div>
        )}

        {/* ── CMS ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/cms") ? styles.active : ""}`}
          onClick={() => setCmsOpen((p) => !p)}
        >
          <LayoutTemplate size={18} />
          {!collapsed && <span>CMS</span>}
          {!collapsed && (cmsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {cmsOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${styles.subitemGroup}`}
              onClick={() => setCmsLayoutOpen((p) => !p)}
            >
              <Settings size={14} />
              Layout
              {cmsLayoutOpen ? <ChevronDown size={13} className={styles.groupChevron} /> : <ChevronRight size={13} className={styles.groupChevron} />}
            </div>

            {cmsLayoutOpen && (
              <div className={styles.submenu}>
                <div
                  className={`${styles.subitem} ${isActive("/cms/layout/header") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/cms/layout/header")}
                >
                  Header
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/cms/layout/footer") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/cms/layout/footer")}
                >
                  Footer
                </div>
                <div
                  className={`${styles.subitem} ${isActive("/cms/layout/homepage") ? styles.activeSub : ""}`}
                  onClick={() => navigate("/cms/layout/homepage")}
                >
                  Homepage
                </div>
              </div>
            )}

            <div
              className={`${styles.subitem} ${isActive("/cms/pages") ? styles.activeSub : ""}`}
              onClick={() => navigate("/cms/pages")}
            >
              <FileText size={14} />
              Pages
            </div>
            <div
              className={`${styles.subitem} ${styles.subitemGroup} ${isActivePrefix("/cms/static-pages") ? styles.activeGroup : ""}`}
              onClick={() => {
                // With no groups yet there is nothing to expand into, so behave
                // like the plain link it used to be.
                if (!staticGroups.length) { navigate("/cms/static-pages"); return; }
                setStaticPagesOpen((p) => !p);
              }}
            >
              <ScrollText size={14} />
              Static Pages
              {staticGroups.length > 0 &&
                (staticPagesOpen
                  ? <ChevronDown size={13} className={styles.groupChevron} />
                  : <ChevronRight size={13} className={styles.groupChevron} />)}
            </div>

            {staticPagesOpen && staticGroups.length > 0 && (
              <div className={styles.submenu}>
                {staticGroups.map((p) => (
                  <div
                    key={p.slug}
                    className={`${styles.subitem} ${
                      location.search.includes(`page=${p.slug}`) ? styles.activeSub : ""
                    }`}
                    onClick={() => navigate(`/cms/static-pages?page=${encodeURIComponent(p.slug)}`)}
                  >
                    {p.title}
                  </div>
                ))}
              </div>
            )}
            {/* FAQs. Hidden for a while because the customer-facing FAQ was a
                section of the About SUNSKY static page, which made this a second
                place to edit the same thing. That is now the other way round:
                the help centre at holidaybooking.be/faq reads THIS screen, the
                About page no longer carries an FAQ block, and hiding the menu
                item left the client with no way to reach the only place the
                questions can be edited. */}
            <div
              className={`${styles.subitem} ${isActive("/cms/faqs") ? styles.activeSub : ""}`}
              onClick={() => navigate("/cms/faqs")}
            >
              <HelpCircle size={14} />
              FAQs
            </div>
            <div
              className={`${styles.subitem} ${isActive("/cms/subscribers") ? styles.activeSub : ""}`}
              onClick={() => navigate("/cms/subscribers")}
            >
              <Mail size={14} />
              Subscribers
            </div>
            {/* SEO Pages. Sits with the other CMS resources because it edits the same
                thing they do: what a public page says. The pages themselves are
                generated automatically from Geo Data, so this screen is for the
                handful worth writing by hand. */}
            <div
              className={`${styles.subitem} ${isActive("/cms/seo") ? styles.activeSub : ""}`}
              onClick={() => navigate("/cms/seo")}
            >
              <Globe size={14} />
              SEO Pages
            </div>
          </div>
        )}

        {/* ── Users & Permissions (temporarily hidden — feature complete, not yet exposed in nav) ──
        <div
          className={`${styles.item} ${isActivePrefix("/users") ? styles.active : ""}`}
          onClick={() => setUsersOpen((p) => !p)}
        >
          <ShieldCheck size={18} />
          {!collapsed && <span>Users & Permissions</span>}
          {!collapsed && (usersOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {usersOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${isActive("/users/overview") ? styles.activeSub : ""}`}
              onClick={() => navigate("/users/overview")}
            >
              <Users size={14} />
              Users
            </div>
            <div
              className={`${styles.subitem} ${isActive("/users/roles") ? styles.activeSub : ""}`}
              onClick={() => navigate("/users/roles")}
            >
              <UserCheck size={14} />
              Roles
            </div>
            <div
              className={`${styles.subitem} ${isActive("/users/permissions") ? styles.activeSub : ""}`}
              onClick={() => navigate("/users/permissions")}
            >
              <KeyRound size={14} />
              Permissions
            </div>
            <div
              className={`${styles.subitem} ${isActive("/users/audit") ? styles.activeSub : ""}`}
              onClick={() => navigate("/users/audit")}
            >
              <ClipboardCheck size={14} />
              Audit Logs
            </div>
          </div>
        )}
        ── end Users & Permissions ── */}

        {/* ── Reports ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/reports") ? styles.active : ""}`}
          onClick={() => setReportsOpen((p) => !p)}
        >
          <FileBarChart size={18} />
          {!collapsed && <span>Reports</span>}
          {!collapsed && (reportsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {reportsOpen && !collapsed && (
          <div className={styles.submenu}>
            <div
              className={`${styles.subitem} ${isActive("/reports/flights") ? styles.activeSub : ""}`}
              onClick={() => navigate("/reports/flights")}
            >
              <Plane size={14} />
              Flight Report
            </div>
            <div
              className={`${styles.subitem} ${isActive("/reports/hotels") ? styles.activeSub : ""}`}
              onClick={() => navigate("/reports/hotels")}
            >
              <Hotel size={14} />
              Hotel Report
            </div>
            <div
              className={`${styles.subitem} ${isActive("/reports/transfers") ? styles.activeSub : ""}`}
              onClick={() => navigate("/reports/transfers")}
            >
              <Car size={14} />
              Transfer Report
            </div>
            <div
              className={`${styles.subitem} ${isActive("/reports/invoices") ? styles.activeSub : ""}`}
              onClick={() => navigate("/reports/invoices")}
            >
              <FileInvoice size={14} />
              Invoice Report
            </div>
            <div
              className={`${styles.subitem} ${isActive("/reports/insurance") ? styles.activeSub : ""}`}
              onClick={() => navigate("/reports/insurance")}
            >
              <ShieldCheck size={14} />
              Insurance Report
            </div>
          </div>
        )}

        {/* ── System Settings ── */}
        <div
          className={`${styles.item} ${isActivePrefix("/system") ? styles.active : ""}`}
          onClick={() => setSystemOpen((p) => !p)}
        >
          <Settings size={18} />
          {!collapsed && <span>System Settings</span>}
          {!collapsed && (systemOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>

        {systemOpen && !collapsed && (
          <div className={styles.submenu}>
            {/* First in the list: this is the page you open when something is wrong, where the
                rest are configuration you open on purpose. */}
            <div
              className={`${styles.subitem} ${isActive("/system/monitoring") ? styles.activeSub : ""}`}
              onClick={() => navigate("/system/monitoring")}
            >
              <Activity size={14} />
              Monitoring
            </div>
            <div
              className={`${styles.subitem} ${isActive("/system/company") ? styles.activeSub : ""}`}
              onClick={() => navigate("/system/company")}
            >
              <Building size={14} />
              Company Settings
            </div>
            <div
              className={`${styles.subitem} ${isActive("/system/sequences") ? styles.activeSub : ""}`}
              onClick={() => navigate("/system/sequences")}
            >
              <Hash size={14} />
              Sequence Settings
            </div>
            <div
              className={`${styles.subitem} ${isActive("/system/api") ? styles.activeSub : ""}`}
              onClick={() => navigate("/system/api")}
            >
              <Server size={14} />
              API Settings
            </div>
            <div
              className={`${styles.subitem} ${isActive("/system/support-levels") ? styles.activeSub : ""}`}
              onClick={() => navigate("/system/support-levels")}
            >
              <LifeBuoy size={14} />
              Support Levels
            </div>
            <div
              className={`${styles.subitem} ${isActive("/system/support-services") ? styles.activeSub : ""}`}
              onClick={() => navigate("/system/support-services")}
            >
              <Wrench size={14} />
              Support Services
            </div>
          </div>
        )}

      </div>

      {!collapsed && (
        <div className={styles.user}>
          <img
            src={user?.avatar || `https://i.pravatar.cc/40?u=${user?.email || "user"}`}
            alt={user?.name || "User"}
          />
          <div>
            <b>{user?.name || "User"}</b>
            <span>{user?.isAdmin ? " Admin" : ` ${user?.roles?.[0]?.name}` || " User"}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;